/**
 * Générateur — Caractériser graphiquement la proportionnalité.
 * Attendu ID : 995 | Niveau : 5ème | Domaine : Fonctions (cluster 3, O15)
 *
 * Plan de cartes validé (doc prof TANOED EXERCISEUR, Fonctions cluster 3) :
 *   - critere (Ex.2 « Trois allures » + Créé n°51 « les deux sens ») :
 *       allures_qcm_multiple (UN SVG composé : droite par l'origine / droite
 *       décalée / courbe — élément additionnel image, canal normatif),
 *       allures_justif_qcm, critere_vf (le distracteur clé : une COURBE passant
 *       par l'origine ne suffit pas), critere_trou_drag
 *   - placer (Créé n°49 « les cerises ») : placer_2d (situation proportionnelle,
 *       affine ou dégressive tirée → la conclusion varie), placer_constat_qcm,
 *       placer_lecture_saisie (proportionnel uniquement, calculable en 5e),
 *       placer_vf
 *   - coefficient (Créé n°52 « lire le coefficient sur la droite ») :
 *       coeff_droites_relier (SVG 3 droites par l'origine, lecture en x = 1),
 *       coeff_saisie, coeff_d4_qcm ((0;b)-(1;b+k) : droite mais pas par
 *       l'origine), coeff_pourquoi_qcm
 *
 * Anti-doublon inter-gens (décision 2026-07-11) : l'Ex.4 « trois tarifs de
 * location » (double étiquette O6/O15) n'est PAS repris ici — gen_985 porte
 * déjà la catégorie tarifs ; le critère est travaillé via allures/coefficient.
 *
 * Invariants : coefficients à une décimale (dixièmes entiers) ; points marqués
 * des SVG toujours à coordonnées lisibles ((1 ; k) ou (2 ; 2k) entier) ;
 * placer_points_2d en coordonnées entières sur graduations, format "x;y".
 * Variantes estimées : > 1500.
 */

import {
    randInt, randomChoice, randomChoiceMultiple, shuffle,
    basePayload, makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier,
    filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 995;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Dixièmes entiers → string français. */
const fmtT = (t) => {
    const int = Math.trunc(t / 10);
    const dec = t % 10;
    return dec === 0 ? String(int) : `${int},${dec}`;
};

const T_TH = 'background:#1E3A8A;color:#fff;padding:6px 14px;border:1px solid #64748b;font-weight:600';
const T_TD = 'padding:6px 14px;border:1px solid #64748b;text-align:center';
const ligneTable = (th, cells) => `<tr><th style="${T_TH}">${th}</th>`
    + cells.map((c) => `<td style="${T_TD}">${c}</td>`).join('') + '</tr>';
const tableEnonce = (...lignes) =>
    `<table style="border-collapse:collapse;margin:12px auto">${lignes.join('')}</table>`;

/** Élément additionnel image (canal normatif §12 FORMATS_TANOED). */
const imgElement = (svg, maxWidth = 'medium') => ({
    version: 1,
    elements: [{ type: 'image', config: {
        imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth,
    } }],
});

/** Payload placer_points_2d — format canonique STRICT "x;y" (§9bis FORMATS_TANOED). */
const makePlacer2D = (texte, repere, points, label) => {
    const bonne = {};
    points.forEach((p) => { bonne[p.label] = `${p.x};${p.y}`; });
    return basePayload('association', texte, {
        subType: 'placer_points_2d',
        repere,
        pointsToPlace: points.map((p) => p.label),
        bonne_reponse: JSON.stringify(bonne),
    }, opts(label));
};

// ═══════════════════════════════════════════════════════════
// SVG (palette chapitre 14 : encre #1e293b, secondaire #475569, données #1E3A8A)
// ═══════════════════════════════════════════════════════════

/** Un panneau graphique 150×150 : axes + une allure ('origine'|'decalee'|'courbe'). */
const panneauAllure = (x0, num, kind, k, b) => {
    const oX = x0 + 24, oY = 118, wP = 108, hP = 96;
    let trace;
    if (kind === 'origine') {
        // Droite passant par l'origine, pente visuelle k ∈ 4..9 (dixièmes de hP)
        const yFin = oY - (hP * k) / 10;
        trace = `<line x1="${oX}" y1="${oY}" x2="${oX + wP}" y2="${yFin}" stroke="#1E3A8A" stroke-width="2.4"/>`;
    } else if (kind === 'decalee') {
        const yDep = oY - (hP * b) / 10;
        const yFin = oY - (hP * Math.min(b + k, 10)) / 10;
        trace = `<line x1="${oX}" y1="${yDep}" x2="${oX + wP}" y2="${yFin}" stroke="#1E3A8A" stroke-width="2.4"/>`;
    } else {
        // Courbe passant par l'origine (allure parabolique, jamais une droite)
        const yFin = oY - (hP * k) / 10;
        trace = `<path d="M ${oX} ${oY} Q ${oX + wP * 0.72} ${oY - hP * 0.06} ${oX + wP} ${yFin}" fill="none" stroke="#1E3A8A" stroke-width="2.4"/>`;
    }
    return `<g>`
        + `<line x1="${oX}" y1="${oY}" x2="${oX + wP + 8}" y2="${oY}" stroke="#1e293b" stroke-width="1.4"/>`
        + `<line x1="${oX}" y1="${oY}" x2="${oX}" y2="${oY - hP - 8}" stroke="#1e293b" stroke-width="1.4"/>`
        + `<polygon points="${oX + wP + 13},${oY} ${oX + wP + 5},${oY - 3.5} ${oX + wP + 5},${oY + 3.5}" fill="#1e293b"/>`
        + `<polygon points="${oX},${oY - hP - 13} ${oX - 3.5},${oY - hP - 5} ${oX + 3.5},${oY - hP - 5}" fill="#1e293b"/>`
        + `<text x="${oX - 6}" y="${oY + 12}" font-size="10" fill="#475569">0</text>`
        + trace
        + `<text x="${oX + wP / 2}" y="${oY + 28}" font-size="12" font-weight="bold" text-anchor="middle" fill="#1e293b">Graphique ${num}</text>`
        + `</g>`;
};

const svgAllures = (kinds, params) => {
    const w = 480, h = 160;
    const panneaux = kinds.map((kind, i) => panneauAllure(10 + i * 158, i + 1, kind, params[i].k, params[i].b));
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" font-family="Arial">${panneaux.join('')}</svg>`;
};

/** 3 droites passant par l'origine, chacune avec un point marqué à coordonnées lisibles. */
const svgDroites = (droites) => {
    const w = 340, h = 250, mL = 40, mB = 32, mT = 14, mR = 14;
    const xmax = 4;
    const ymax = Math.max(...droites.map((d) => (d.k10 * xmax) / 10)) * 1.08;
    const X = (x) => mL + (x / xmax) * (w - mL - mR);
    const Y = (y) => (h - mB) - (y / ymax) * (h - mB - mT);
    const x0 = mL, y0 = h - mB;
    const couleurs = ['#1E3A8A', '#b45309', '#475569'];
    const traces = droites.map((d, i) => {
        const k = d.k10 / 10;
        const px = d.pt[0], py = d.pt[1];
        return `<line x1="${x0}" y1="${y0}" x2="${X(xmax)}" y2="${Y(k * xmax)}" stroke="${couleurs[i]}" stroke-width="2.2"/>`
            + `<circle cx="${X(px)}" cy="${Y(py)}" r="4" fill="${couleurs[i]}" stroke="#fff" stroke-width="1.2"/>`
            + `<text x="${X(px) + 7}" y="${Y(py) - 6}" font-size="11" fill="${couleurs[i]}">(${px} ; ${fmtT(py * 10)})</text>`
            + `<text x="${X(xmax) - 24}" y="${Y(k * xmax) - 7}" font-size="12" font-weight="bold" fill="${couleurs[i]}">(d${'₁₂₃'[i]})</text>`;
    });
    // Graduations unitaires sur l'axe des abscisses (lecture en x = 1 et x = 2)
    const grads = [1, 2, 3].map((g) =>
        `<line x1="${X(g)}" y1="${y0 - 3}" x2="${X(g)}" y2="${y0 + 3}" stroke="#1e293b"/>`
        + `<text x="${X(g)}" y="${y0 + 14}" font-size="10" text-anchor="middle" fill="#475569">${g}</text>`).join('');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" font-family="Arial">`
        + `<line x1="${x0}" y1="${y0}" x2="${w - 6}" y2="${y0}" stroke="#1e293b" stroke-width="1.4"/>`
        + `<line x1="${x0}" y1="${y0}" x2="${x0}" y2="8" stroke="#1e293b" stroke-width="1.4"/>`
        + `<polygon points="${w - 2},${y0} ${w - 9},${y0 - 3.5} ${w - 9},${y0 + 3.5}" fill="#1e293b"/>`
        + `<polygon points="${x0},4 ${x0 - 3.5},11 ${x0 + 3.5},11" fill="#1e293b"/>`
        + `<text x="${x0 - 7}" y="${y0 + 13}" font-size="10" fill="#475569">0</text>`
        + grads + traces.join('')
        + `</svg>`;
};

// ═══════════════════════════════════════════════════════════
// 1. CRITERE — trois allures et les deux sens (Ex.2 + Créé n°51)
// ═══════════════════════════════════════════════════════════

/** Tire 3 panneaux dont 1 ou 2 « droite par l'origine ». */
const tirerAllures = () => {
    const nOrigine = randomChoice([1, 1, 2]); // 2 bonnes réponses 1 fois sur 3
    const autres = shuffle(['decalee', 'courbe']);
    const kinds = shuffle([
        ...Array.from({ length: nOrigine }, () => 'origine'),
        ...autres.slice(0, 3 - nOrigine),
    ]);
    const params = kinds.map(() => ({ k: randInt(4, 9), b: randInt(2, 4) }));
    return { kinds, params };
};

const genAlluresQcmMultiple = () => {
    const { kinds, params } = tirerAllures();
    const choix = kinds.map((_, i) => `Graphique ${i + 1}`);
    const bonnes = kinds
        .map((kind, i) => (kind === 'origine' ? `Graphique ${i + 1}` : null))
        .filter(Boolean);
    return {
        ...makeQcm(choix, bonnes,
            `<p>Voici trois graphiques ci-dessous. Le(s)quel(s) peut (peuvent) représenter une situation de proportionnalité ?</p>`,
            opts('Reconnaître les allures proportionnelles'), true),
        elements_additionnels: imgElement(svgAllures(kinds, params)),
    };
};

const genAlluresJustifQcm = () => {
    const { kinds, params } = tirerAllures();
    const cible = randInt(0, 2);
    const kind = kinds[cible];
    const questions = {
        origine: {
            q: `Pourquoi le graphique ${cible + 1} peut-il représenter une proportionnalité ?`,
            bonne: 'C’est une droite ET elle passe par l’origine',
            dist: ['C’est une droite, cela suffit', 'Il passe par l’origine, cela suffit', 'Il monte régulièrement'],
        },
        decalee: {
            q: `Pourquoi le graphique ${cible + 1} ne représente-t-il pas une proportionnalité ?`,
            bonne: 'C’est une droite, mais elle ne passe pas par l’origine',
            dist: ['Ce n’est pas une droite', 'Il ne monte pas assez vite', 'Il passe par l’origine'],
        },
        courbe: {
            q: `Pourquoi le graphique ${cible + 1} ne représente-t-il pas une proportionnalité ?`,
            bonne: 'Ce n’est pas une droite (même s’il passe par l’origine)',
            dist: ['Il ne passe pas par l’origine', 'Il descend', 'C’est une droite qui ne passe pas par l’origine'],
        },
    };
    const spec = questions[kind];
    return {
        ...makeQcm([spec.bonne, ...spec.dist], spec.bonne,
            `<p>Observe les trois graphiques ci-dessous. ${spec.q}</p>`,
            opts('Justifier avec le critère graphique')),
        elements_additionnels: imgElement(svgAllures(kinds, params)),
    };
};

const genCritereVF = () => {
    const n = randInt(2, 6);
    const m = randomChoice([2, 3, 4]) * n;
    const b = randInt(1, 4);
    const pool = [
        [`Une droite qui passe par (0 ; 0) et par (${n} ; ${m}) représente une situation de proportionnalité`, 0],
        ['Une courbe qui passe par l’origine représente une situation de proportionnalité', 1],
        ['Toute situation de proportionnalité se représente par une droite passant par l’origine', 0],
        [`Une droite qui coupe l’axe des ordonnées en ${b} (et non en 0) représente une proportionnalité`, 1],
        ['Si des points sont alignés entre eux mais pas avec l’origine, la situation n’est pas proportionnelle', 0],
        ['Pour représenter une proportionnalité, il suffit que le graphique passe par l’origine', 1],
    ];
    const lignes = randomChoiceMultiple(pool, 4);
    return makeVraiFaux(lignes.map((l) => l[0]), lignes.map((l) => l[1]),
        `<p>Vrai ou faux ? (le critère graphique de la proportionnalité, dans les deux sens)</p>`,
        opts('Le critère dans les deux sens'));
};

const genCritereTrouDrag = () => {
    const kAff = fmtT(randomChoice([20, 30, 40, 50, 60, 70, 15, 25, 35, 45, 55, 65]));
    const formulations = [
        {
            phrase: 'Elle se représente par une {{droite}} qui passe par l’{{origine}} du repère.',
            texte: `<p>On représente graphiquement la situation de proportionnalité y = ${kAff} × x.</p>`,
        },
        {
            phrase: 'Une {{droite}} passant par l’{{origine}} représente une situation de {{proportionnalité}}.',
            texte: `<p>Complète la propriété du cours (exemple : la situation y = ${kAff} × x).</p>`,
        },
        {
            phrase: 'Si les points sont alignés entre eux et avec l’{{origine}}, alors la situation est {{proportionnelle}}.',
            texte: `<p>On a placé les points d’un tableau de valeurs (par exemple celui de y = ${kAff} × x). Complète la conclusion du cours.</p>`,
        },
    ];
    const f = randomChoice(formulations);
    const candidats = ['courbe', 'sommet', 'extrémité', 'symétrie', 'flèche'];
    const distracteurs = filtrerDistracteurs(f.phrase, candidats).slice(0, 3);
    return makeTexteTrou(f.phrase, f.texte,
        { mode: 'drag', distracteurs, ...opts('Compléter le critère graphique') });
};

// ═══════════════════════════════════════════════════════════
// 2. PLACER — placer les points puis conclure (Créé n°49)
// ═══════════════════════════════════════════════════════════

const tirerSituationPlacer = () => {
    const kind = randomChoice(['prop', 'affine', 'degressif']);
    if (kind === 'prop') {
        const k = randomChoice([2, 3, 4, 5]);
        const ctx = randomChoice([
            { nom: 'Le prix des cerises est proportionnel à la masse achetée', x: 'masse (kg)', y: 'prix (€)' },
            { nom: 'Le prix du jus de pomme est proportionnel au volume acheté', x: 'volume (L)', y: 'prix (€)' },
            { nom: 'La distance parcourue est proportionnelle à la durée (vitesse constante)', x: 'durée (min)', y: 'distance (km)' },
        ]);
        const xs = [1, 2, 3, 5];
        return { kind, k, ctx, xs, ys: xs.map((x) => k * x) };
    }
    if (kind === 'affine') {
        const k = randomChoice([1, 2, 3]);
        const b = randomChoice([2, 3, 4]);
        const ctx = randomChoice([
            { nom: 'Une location de vélo coûte un forfait fixe plus un prix par heure', x: 'durée (h)', y: 'prix (€)' },
            { nom: 'Une livraison coûte des frais fixes plus un prix par kilogramme', x: 'masse (kg)', y: 'prix (€)' },
        ]);
        const xs = [1, 2, 3, 5];
        return { kind, k, b, ctx, xs, ys: xs.map((x) => k * x + b) };
    }
    const motif = randomChoice([
        { xs: [1, 2, 4, 6], ys: [5, 9, 16, 22] },
        { xs: [1, 2, 3, 6], ys: [6, 11, 15, 27] },
        { xs: [1, 3, 5, 6], ys: [4, 10, 15, 17] },
    ]);
    const ctx = randomChoice([
        { nom: 'Un imprimeur applique un tarif dégressif pour les photocopies', x: 'nombre de copies (dizaines)', y: 'prix (€)' },
        { nom: 'Un tarif de tickets devient dégressif quand on en achète beaucoup', x: 'nombre de tickets', y: 'prix (€)' },
    ]);
    return { kind: 'degressif', ctx, xs: motif.xs, ys: motif.ys };
};

const tablePlacer = (sit, avecPoints = false) => {
    const lignes = [
        ligneTable(sit.ctx.x, sit.xs.map(String)),
        ligneTable(sit.ctx.y, sit.ys.map(String)),
    ];
    if (avecPoints) lignes.unshift(ligneTable('Point', sit.xs.map((_, i) => 'ABCD'[i])));
    return tableEnonce(...lignes);
};

const reperePlacer = (sit) => ({
    xMax: Math.max(...sit.xs) + 1,
    yMax: Math.min(40, Math.ceil((Math.max(...sit.ys) + 1) / 5) * 5),
    xStep: 1,
    yStep: 1,
    xLabel: sit.ctx.x,
    yLabel: sit.ctx.y,
    grille: true,
});

const genPlacer2D = () => {
    const sit = tirerSituationPlacer();
    const points = sit.xs.map((x, i) => ({ label: 'ABCD'[i], x, y: sit.ys[i] }));
    return makePlacer2D(
        `<p>${sit.ctx.nom}. Voici le tableau ci-dessous :</p>\n${tablePlacer(sit, true)}\n<p>Place les points A, B, C et D dans le repère.</p>`,
        reperePlacer(sit), points, 'Placer les points de la situation',
    );
};

const genPlacerConstatQcm = () => {
    const sit = tirerSituationPlacer();
    const reponses = {
        prop: 'Les points sont alignés entre eux et avec l’origine : la situation est proportionnelle',
        affine: 'Les points sont alignés entre eux mais pas avec l’origine : la situation n’est pas proportionnelle',
        degressif: 'Les points ne sont pas alignés : la situation n’est pas proportionnelle',
    };
    const bonne = reponses[sit.kind];
    return makeQcm([reponses.prop, reponses.affine, reponses.degressif], bonne,
        `<p>${sit.ctx.nom}. On a placé dans un repère les points de ce tableau :</p>\n${tablePlacer(sit)}\n<p>Que constate-t-on ?</p>`,
        opts('Conclure sur l’alignement des points'));
};

const genPlacerLectureSaisie = () => {
    // Toujours le cas PROPORTIONNEL (calculable en 5e sans ajustement affine)
    const k = randomChoice([2, 3, 4, 5]);
    const ctx = randomChoice([
        { nom: 'Le prix des cerises est proportionnel à la masse achetée', x: 'masse (kg)', y: 'prix (€)' },
        { nom: 'Le prix du jus de pomme est proportionnel au volume acheté', x: 'volume (L)', y: 'prix (€)' },
        { nom: 'La distance parcourue est proportionnelle à la durée (vitesse constante)', x: 'durée (min)', y: 'distance (km)' },
    ]);
    const xs = [1, 2, 3, 5];
    const s = { kind: 'prop', k, ctx, xs, ys: xs.map((x) => k * x) };
    const xq = randomChoice([4, 6, 7]);
    return makeSaisie(String(k * xq),
        `<p>${s.ctx.nom}. Voici le tableau ci-dessous :</p>\n${tablePlacer(s)}\n<p>Sur le graphique, on lirait la valeur pour ${xq} en abscisse. Calcule-la.</p>`,
        opts('Lire/calculer une valeur sur la droite'));
};

const genPlacerVF = () => {
    const sit = tirerSituationPlacer();
    const i1 = 1;
    const doubleOk = sit.ys[1] === 2 * sit.ys[0] && sit.xs[1] === 2 * sit.xs[0];
    const pool = [
        ['La situation est proportionnelle', sit.kind === 'prop' ? 0 : 1],
        ['Reliés, les points forment une droite passant par l’origine', sit.kind === 'prop' ? 0 : 1],
        [`Pour ${sit.xs[i1]} en abscisse, l’ordonnée est ${sit.ys[i1]}`, 0],
        [`Pour ${sit.xs[2]} en abscisse, l’ordonnée est ${sit.ys[2] + randomChoice([1, 2])}`, 1],
        [`Quand l’abscisse passe de ${sit.xs[0]} à ${sit.xs[1]}, l’ordonnée double`, doubleOk ? 0 : 1],
    ];
    const lignes = randomChoiceMultiple(pool, 4);
    return makeVraiFaux(lignes.map((l) => l[0]), lignes.map((l) => l[1]),
        `<p>${sit.ctx.nom}. Voici le tableau ci-dessous :</p>\n${tablePlacer(sit)}\n<p>Vrai ou faux ?</p>`,
        opts('Vrai/faux sur la situation représentée'));
};

// ═══════════════════════════════════════════════════════════
// 3. COEFFICIENT — lire le coefficient sur la droite (Créé n°52)
// ═══════════════════════════════════════════════════════════

const K10_POOL = [15, 20, 25, 30, 35, 40, 45, 50, 55, 60];

/** Point lisible pour un coefficient : (1 ; k) si k entier, sinon (2 ; 2k). */
const pointLisible = (k10) => (k10 % 10 === 0 ? [1, k10 / 10] : [2, k10 / 5]);

const tirerDroites = () => {
    const k10s = randomChoiceMultiple(K10_POOL, 3);
    return k10s.map((k10) => ({ k10, pt: pointLisible(k10) }));
};

const genCoeffDroitesRelier = () => {
    const droites = tirerDroites();
    const pairs = droites.map((d, i) => ({
        source: `(d${'₁₂₃'[i]})`,
        target: `coefficient ${fmtT(d.k10)}`,
    }));
    return {
        ...makeRelier(pairs,
            `<p>Le graphique ci-dessous montre trois droites passant par l’origine. Un point de chaque droite est marqué avec ses coordonnées. Relie chaque droite à son coefficient de proportionnalité.</p>`,
            opts('Associer chaque droite à son coefficient')),
        elements_additionnels: imgElement(svgDroites(droites)),
    };
};

const genCoeffSaisie = () => {
    const k10 = randomChoice(K10_POOL);
    const a = randomChoice([2, 4]);
    const b10 = a * k10;
    const formulation = randomChoice([
        `<p>Une droite passe par l’origine et par le point (${a} ; ${fmtT(b10)}). Quel est le coefficient de proportionnalité de la situation qu’elle représente ?</p>`,
        `<p>Sur un graphique, une situation de proportionnalité est représentée par une droite qui passe par l’origine et par le point (${a} ; ${fmtT(b10)}). Quel est son coefficient de proportionnalité ?</p>`,
    ]);
    return makeSaisie(fmtT(k10), formulation,
        opts('Calculer le coefficient depuis un point'));
};

const genCoeffD4Qcm = () => {
    const b = randInt(1, 6);
    const k = randInt(1, 4);
    const bonne = `Non : c’est une droite, mais elle ne passe pas par l’origine (elle coupe l’axe des ordonnées en ${b})`;
    const dist = [
        'Oui : c’est une droite, cela suffit',
        `Oui : elle passe par le point (1 ; ${b + k})`,
        'Non : ce n’est pas une droite',
    ];
    return makeQcm([bonne, ...dist], bonne,
        `<p>La droite (d₄) passe par les points (0 ; ${b}) et (1 ; ${b + k}). Représente-t-elle une situation de proportionnalité ?</p>`,
        opts('La droite qui ne passe pas par l’origine'));
};

const genCoeffPourquoiQcm = () => {
    const droites = tirerDroites();
    const bonne = 'Ce sont des droites et elles passent par l’origine';
    const dist = [
        'Ce sont des droites, cela suffit',
        'Elles montent toutes',
        `Elles passent toutes par le point (1 ; ${fmtT(droites[0].k10)})`,
    ];
    return {
        ...makeQcm([bonne, ...dist], bonne,
            `<p>Observe le graphique ci-dessous. Pourquoi ces trois droites représentent-elles des situations de proportionnalité ?</p>`,
            opts('Justifier : pourquoi ces droites')),
        elements_additionnels: imgElement(svgDroites(droites)),
    };
};

// ═══════════════════════════════════════════════════════════
// Registre + contrat générateur standard
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    allures_qcm_multiple: genAlluresQcmMultiple,
    allures_justif_qcm: genAlluresJustifQcm,
    critere_vf: genCritereVF,
    critere_trou_drag: genCritereTrouDrag,

    placer_2d: genPlacer2D,
    placer_constat_qcm: genPlacerConstatQcm,
    placer_lecture_saisie: genPlacerLectureSaisie,
    placer_vf: genPlacerVF,

    coeff_droites_relier: genCoeffDroitesRelier,
    coeff_saisie: genCoeffSaisie,
    coeff_d4_qcm: genCoeffD4Qcm,
    coeff_pourquoi_qcm: genCoeffPourquoiQcm,
};

const FORMAT_LABELS = {
    qcm_multiple: 'Trois allures (QCM multiple)',
    justif_qcm: 'Justifier (QCM)',
    vf: 'Vrai/Faux',
    trou_drag: 'Compléter (glisser)',
    '2d': 'Placer dans le repère',
    constat_qcm: 'Conclure (QCM)',
    lecture_saisie: 'Lire une valeur (saisie)',
    droites_relier: 'Relier droite et coefficient',
    saisie: 'Coefficient (saisie)',
    d4_qcm: 'Droite décalée (QCM)',
    pourquoi_qcm: 'Justifier (QCM)',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS)
        .filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({
            key: k,
            label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k,
        }));

export const CATEGORIES = [
    {
        id: 'critere',
        label: 'Le critère graphique (droite passant par l’origine)',
        formatsList: [...buildFormats('allures'), ...buildFormats('critere')],
    },
    {
        id: 'placer',
        label: 'Placer les points et conclure',
        formatsList: buildFormats('placer'),
    },
    {
        id: 'coefficient',
        label: 'Lire le coefficient sur la droite',
        formatsList: buildFormats('coeff'),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length
            ? randomChoice(matching)
            : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
